import React from 'react';
import {
  Alert,
  Box,
  Card,
  Chip,
  Collapse,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import PaymentsIcon from '@mui/icons-material/Payments';
import LanguageIcon from '@mui/icons-material/Language';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';

const formatMoney = (val) => {
  const num = typeof val === 'number' ? val : parseFloat(val);
  return Number.isNaN(num)
    ? '0.00'
    : num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatInt = (val) => {
  const num = typeof val === 'number' ? val : parseInt(val, 10);
  return Number.isNaN(num) ? String(val || 0) : num.toLocaleString('en-IN');
};

const renderMetricBadge = (value, isNotCreated) => {
  if (isNotCreated) {
    return (
      <Chip
        label="Not Created"
        size="small"
        sx={{ bgcolor: '#fee2e2', color: '#991b1b', fontWeight: 700, fontSize: '0.7rem', height: 20 }}
      />
    );
  }
  if (value === 'Never Used') {
    return (
      <Chip
        label="Never Used"
        size="small"
        sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 700, fontSize: '0.7rem', height: 20 }}
      />
    );
  }
  if (value === null || value === undefined || value === '' || value === '-') {
    return (
      <Typography variant="body2" color="text.secondary">
        -
      </Typography>
    );
  }
  if (typeof value === 'number') {
    return (
      <Typography variant="body2" fontWeight={700} color="#0f172a">
        {value}
      </Typography>
    );
  }
  return (
    <Typography variant="body2" fontWeight={600} color="#1e293b">
      {String(value)}
    </Typography>
  );
};

export default function CompanyReportDetailsTab({
  company,
  reports = [],
  reportColumns = [],
  reportTabs = [],
  companyId,
}) {
  const availableTabs = React.useMemo(() => {
    const set = new Set();
    reports.forEach((r) => {
      if (r.tab_name) set.add(r.tab_name);
    });
    reportTabs.forEach((t) => set.add(t));
    if (set.size === 0) set.add('2026 - 2027');
    return Array.from(set);
  }, [reports, reportTabs]);

  const [selectedYear, setSelectedYear] = React.useState(
    availableTabs[0] || '2026 - 2027'
  );

  React.useEffect(() => {
    if (availableTabs.length > 0 && !availableTabs.includes(selectedYear)) {
      setSelectedYear(availableTabs[0]);
    }
  }, [availableTabs, selectedYear]);

  const [showTechnicalDetails, setShowTechnicalDetails] = React.useState(false);

  // Find report row for selected year, or construct fallback
  const activeRow = React.useMemo(() => {
    const matched = reports.find((r) => (r.tab_name || '').toLowerCase() === selectedYear.toLowerCase());
    if (matched) return matched;
    if (reports.length > 0) return reports[0];
    return {
      id: `fallback-${companyId}`,
      institute_name: company?.name || 'Institution',
      school_address: company?.address || '',
      tab_name: selectedYear,
      is_not_created: true,
      using_efficiency: 0,
      payment_gateway_enabled: false,
      website_enabled: false,
      biometric_enabled: false,
      coordinator_name: company?.coordinator?.username || 'Not Assigned',
      status_by_team: 'Active',
      payment_gateway_data: {
        total_amount: 0,
        transaction_count: 0,
        total_transaction_fees: 0,
        month_count: 0,
      },
      report_data: {},
    };
  }, [reports, selectedYear, company, companyId]);

  const repData = activeRow.report_data || activeRow;

  const clientChartData = React.useMemo(() => {
    if (!activeRow || activeRow.is_not_created) return [];
    return [
      {
        name: 'Attnd',
        value: parseFloat(repData.attendance_used_percentage || 0),
        fill: '#3b82f6',
      },
      {
        name: 'Diary',
        value: parseFloat(repData.diary_used_percentage || 0),
        fill: '#8b5cf6',
      },
      {
        name: 'Exam',
        value: parseFloat(repData.last_exam_used_percentage || 0),
        fill: '#10b981',
      },
      {
        name: 'Fees',
        value: parseFloat(repData.fee_collection_used_percentage || 0),
        fill: '#f59e0b',
      },
      {
        name: 'Staff',
        value: parseFloat(repData.staff_attendance_used_percentage || 0),
        fill: '#ec4899',
      },
    ];
  }, [activeRow, repData]);

  const efficiencyVal = parseFloat(activeRow.using_efficiency || 0);

  return (
    <Box sx={{ p: { xs: 1.5, md: 2.5 } }}>
      {/* Institution Primary Header Card */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 3,
          borderRadius: 3,
          backgroundColor: '#f8fafc',
          border: '1px solid #e2e8f0',
          boxShadow: '0 2px 10px rgba(15, 23, 42, 0.04)',
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          flexWrap="wrap"
          gap={2}
        >
          <Box sx={{ flex: 1, minWidth: 260 }}>
            <Box display="flex" alignItems="center" gap={1.2} mb={0.8}>
              <SchoolOutlinedIcon sx={{ color: '#2563eb', fontSize: 28 }} />
              <Typography variant="h5" fontWeight={800} color="#0f172a">
                {activeRow.institute_name || company?.name || 'Institution'}
              </Typography>
            </Box>

            <Box display="flex" alignItems="center" gap={1} color="#64748b" mb={1.5} fontSize="0.875rem">
              <LocationOnOutlinedIcon sx={{ fontSize: 16 }} />
              <Typography variant="body2" color="text.secondary">
                {activeRow.school_address || company?.address || 'Address not configured'}
              </Typography>
            </Box>

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
              <Chip
                size="small"
                icon={<PersonOutlineIcon sx={{ fontSize: 15 }} />}
                label={`Coordinator: ${activeRow.coordinator_name || 'Not Assigned'}`}
                sx={{
                  bgcolor: '#ffffff',
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  border: '1px solid #cbd5e1',
                }}
              />
              <Chip
                size="small"
                label={`Year: ${activeRow.tab_name || selectedYear}`}
                sx={{ bgcolor: '#eff6ff', color: '#1d4ed8', fontWeight: 700, fontSize: '0.75rem' }}
              />
              {activeRow.is_not_created ? (
                <Chip
                  size="small"
                  label="Report Not Created"
                  sx={{ bgcolor: '#fee2e2', color: '#991b1b', fontWeight: 700, fontSize: '0.75rem' }}
                />
              ) : (
                <Chip
                  size="small"
                  label="Report Active"
                  sx={{ bgcolor: '#dcfce7', color: '#15803d', fontWeight: 700, fontSize: '0.75rem' }}
                />
              )}
              {activeRow.status_by_team && (
                <Chip
                  size="small"
                  label={`Team: ${activeRow.status_by_team}`}
                  sx={{
                    bgcolor:
                      activeRow.status_by_team === 'Dead'
                        ? '#fee2e2'
                        : activeRow.status_by_team === 'Risk'
                          ? '#ffedd5'
                          : '#f1f5f9',
                    color:
                      activeRow.status_by_team === 'Dead'
                        ? '#dc2626'
                        : activeRow.status_by_team === 'Risk'
                          ? '#ea580c'
                          : '#334155',
                    fontWeight: 700,
                    fontSize: '0.75rem',
                  }}
                />
              )}
            </Stack>
          </Box>

          {/* Academic Year Switcher & Overall Efficiency Score */}
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
            {availableTabs.length > 1 && (
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel id="acad-year-select-label">Academic Year</InputLabel>
                <Select
                  labelId="acad-year-select-label"
                  value={selectedYear}
                  label="Academic Year"
                  onChange={(e) => setSelectedYear(e.target.value)}
                  sx={{ borderRadius: 2, fontWeight: 700, bgcolor: '#ffffff' }}
                >
                  {availableTabs.map((tab) => (
                    <MenuItem key={tab} value={tab} sx={{ fontWeight: 600 }}>
                      {tab}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <Box
              sx={{
                p: 1.8,
                borderRadius: 2.5,
                backgroundColor: '#ffffff',
                border: '1px solid #e2e8f0',
                textAlign: 'center',
                minWidth: 140,
                boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
              }}
            >
              <Typography
                variant="caption"
                fontWeight={700}
                color="#64748b"
                textTransform="uppercase"
                fontSize="0.68rem"
              >
                Using Efficiency
              </Typography>
              <Typography
                variant="h4"
                fontWeight={900}
                color={activeRow.is_not_created ? '#ea580c' : efficiencyVal > 50 ? '#15803d' : '#ea580c'}
                my={0.2}
              >
                {activeRow.is_not_created ? '0%' : `${efficiencyVal}%`}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={activeRow.is_not_created ? 0 : efficiencyVal}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: '#e2e8f0',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: efficiencyVal > 50 ? '#22c55e' : '#f97316',
                  },
                }}
              />
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Institution Features Adoption Overview */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          borderRadius: 2.5,
          border: '1px solid #e2e8f0',
          backgroundColor: '#ffffff',
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
          <Typography variant="subtitle2" fontWeight={800} color="#0f172a">
            Institution Features Status
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Configured enterprise modules & add-ons
          </Typography>
        </Box>

        <Grid container spacing={2}>
          {/* Payment Gateway Feature */}
          <Grid item xs={12} sm={4}>
            <Box
              sx={{
                p: 1.8,
                borderRadius: 2,
                border: '1px solid',
                borderColor: activeRow.payment_gateway_enabled ? '#bbf7d0' : '#e2e8f0',
                backgroundColor: activeRow.payment_gateway_enabled ? '#f0fdf4' : '#f8fafc',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Box display="flex" alignItems="center" gap={1.2}>
                <PaymentsIcon
                  sx={{
                    color: activeRow.payment_gateway_enabled ? '#16a34a' : '#94a3b8',
                    fontSize: 24,
                  }}
                />
                <Box>
                  <Typography variant="body2" fontWeight={700} color="#0f172a">
                    Payment Gateway
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Online Fee Collection
                  </Typography>
                </Box>
              </Box>
              <Chip
                size="small"
                label={activeRow.payment_gateway_enabled ? 'Enabled' : 'Disabled'}
                sx={{
                  fontWeight: 800,
                  fontSize: '0.72rem',
                  height: 24,
                  bgcolor: activeRow.payment_gateway_enabled ? '#dcfce7' : '#e2e8f0',
                  color: activeRow.payment_gateway_enabled ? '#15803d' : '#64748b',
                }}
              />
            </Box>
          </Grid>

          {/* Website Feature */}
          <Grid item xs={12} sm={4}>
            <Box
              sx={{
                p: 1.8,
                borderRadius: 2,
                border: '1px solid',
                borderColor: activeRow.website_enabled ? '#bfdbfe' : '#e2e8f0',
                backgroundColor: activeRow.website_enabled ? '#eff6ff' : '#f8fafc',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Box display="flex" alignItems="center" gap={1.2}>
                <LanguageIcon
                  sx={{
                    color: activeRow.website_enabled ? '#2563eb' : '#94a3b8',
                    fontSize: 24,
                  }}
                />
                <Box>
                  <Typography variant="body2" fontWeight={700} color="#0f172a">
                    Website
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    School Web Portal
                  </Typography>
                </Box>
              </Box>
              <Chip
                size="small"
                label={activeRow.website_enabled ? 'Enabled' : 'Disabled'}
                sx={{
                  fontWeight: 800,
                  fontSize: '0.72rem',
                  height: 24,
                  bgcolor: activeRow.website_enabled ? '#dbeafe' : '#e2e8f0',
                  color: activeRow.website_enabled ? '#1d4ed8' : '#64748b',
                }}
              />
            </Box>
          </Grid>

          {/* Biometric Feature */}
          <Grid item xs={12} sm={4}>
            <Box
              sx={{
                p: 1.8,
                borderRadius: 2,
                border: '1px solid',
                borderColor: activeRow.biometric_enabled ? '#e9d5ff' : '#e2e8f0',
                backgroundColor: activeRow.biometric_enabled ? '#faf5ff' : '#f8fafc',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Box display="flex" alignItems="center" gap={1.2}>
                <FingerprintIcon
                  sx={{
                    color: activeRow.biometric_enabled ? '#9333ea' : '#94a3b8',
                    fontSize: 24,
                  }}
                />
                <Box>
                  <Typography variant="body2" fontWeight={700} color="#0f172a">
                    Biometric
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Attendance Devices
                  </Typography>
                </Box>
              </Box>
              <Chip
                size="small"
                label={activeRow.biometric_enabled ? 'Enabled' : 'Disabled'}
                sx={{
                  fontWeight: 800,
                  fontSize: '0.72rem',
                  height: 24,
                  bgcolor: activeRow.biometric_enabled ? '#f3e8ff' : '#e2e8f0',
                  color: activeRow.biometric_enabled ? '#7e22ce' : '#64748b',
                }}
              />
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Payment Gateway Usage & Collections Report */}
      <Paper
        elevation={0}
        sx={{
          p: 2.2,
          mb: 3,
          borderRadius: 2.5,
          border: '1px solid #e2e8f0',
          backgroundColor: '#ffffff',
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} mb={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <AccountBalanceWalletOutlinedIcon sx={{ color: '#059669', fontSize: 24 }} />
            <Box>
              <Typography variant="subtitle1" fontWeight={800} color="#0f172a">
                Payment Gateway Report
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Synced transaction volumes, fee collections, and payment modes
              </Typography>
            </Box>
          </Box>
          <Stack direction="row" spacing={1}>
            <Chip
              size="small"
              label={activeRow.payment_gateway_enabled ? 'Gateway Enabled' : 'Gateway Disabled'}
              sx={{
                fontWeight: 800,
                fontSize: '0.72rem',
                bgcolor: activeRow.payment_gateway_enabled ? '#dcfce7' : '#f1f5f9',
                color: activeRow.payment_gateway_enabled ? '#15803d' : '#64748b',
              }}
            />
            {Boolean(activeRow.payment_gateway_data?.month_count) && (
              <Chip
                size="small"
                label={`${activeRow.payment_gateway_data.month_count} Periods Recorded`}
                sx={{
                  fontWeight: 700,
                  fontSize: '0.72rem',
                  bgcolor: '#eff6ff',
                  color: '#1d4ed8',
                }}
              />
            )}
          </Stack>
        </Box>

        {/* Summary KPI Cards */}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Box
              sx={{
                p: 1.8,
                borderRadius: 2,
                backgroundColor: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderLeftWidth: 4,
                borderLeftColor: '#2563eb',
              }}
            >
              <Typography variant="caption" color="text.secondary" fontWeight={700} display="block">
                TOTAL AMOUNT COLLECTED
              </Typography>
              <Typography variant="h6" fontWeight={800} color="#0f172a" mt={0.3}>
                ₹ {formatMoney(activeRow.payment_gateway_data?.total_amount || 0)}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Box
              sx={{
                p: 1.8,
                borderRadius: 2,
                backgroundColor: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderLeftWidth: 4,
                borderLeftColor: '#16a34a',
              }}
            >
              <Typography variant="caption" color="text.secondary" fontWeight={700} display="block">
                TOTAL TRANSACTIONS
              </Typography>
              <Typography variant="h6" fontWeight={800} color="#0f172a" mt={0.3}>
                {formatInt(activeRow.payment_gateway_data?.transaction_count || 0)}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Box
              sx={{
                p: 1.8,
                borderRadius: 2,
                backgroundColor: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderLeftWidth: 4,
                borderLeftColor: '#ea580c',
              }}
            >
              <Typography variant="caption" color="text.secondary" fontWeight={700} display="block">
                GATEWAY CHARGES / FEES
              </Typography>
              <Typography variant="h6" fontWeight={800} color="#0f172a" mt={0.3}>
                ₹ {formatMoney(activeRow.payment_gateway_data?.total_transaction_fees || 0)}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* When not created, show clean warning alert */}
      {activeRow.is_not_created ? (
        <Alert severity="warning" sx={{ mb: 3, borderRadius: 2.5, p: 2 }}>
          <Typography variant="subtitle2" fontWeight={800}>
            This institution is not yet created in the client track report for academic year {activeRow.tab_name || selectedYear}.
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Usage and attendance data will populate once this institution has been synchronized. You can also select another Academic Year above to review historical reports.
          </Typography>
        </Alert>
      ) : (
        <>
          {/* Feature Utilization Rates (%) BarChart */}
          <Paper
            elevation={0}
            sx={{
              p: 2.2,
              mb: 3,
              borderRadius: 2.5,
              border: '1px solid #e2e8f0',
              backgroundColor: '#f8fafc',
            }}
          >
            <Typography variant="subtitle2" fontWeight={800} color="#0f172a" mb={1.5}>
              Feature Utilization Rates (%)
            </Typography>
            <Box sx={{ width: '100%', height: 170 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={clientChartData} margin={{ top: 10, right: 20, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 12, fontWeight: 600 }} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                  <RechartsTooltip formatter={(val) => `${val}%`} />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                    {clientChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>

          {/* Vital Numbers Strip */}
          <Grid container spacing={2} mb={3}>
            <Grid item xs={6} sm={3}>
              <Paper elevation={0} sx={{ p: 1.8, borderRadius: 2, border: '1px solid #e2e8f0', backgroundColor: '#ffffff' }}>
                <Typography variant="caption" color="text.secondary" fontWeight={700} fontSize="0.7rem">
                  STUDENT COUNT
                </Typography>
                <Typography variant="h6" fontWeight={800} color="#0f172a">
                  {repData.student_count || repData.total_students || '-'}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Paper elevation={0} sx={{ p: 1.8, borderRadius: 2, border: '1px solid #e2e8f0', backgroundColor: '#ffffff' }}>
                <Typography variant="caption" color="text.secondary" fontWeight={700} fontSize="0.7rem">
                  LAST ATTENDANCE
                </Typography>
                <Typography variant="body2" fontWeight={700} color="#15803d" noWrap>
                  {repData.student_attendance_last_used_value || repData.attendance_last_used || '-'}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Paper elevation={0} sx={{ p: 1.8, borderRadius: 2, border: '1px solid #e2e8f0', backgroundColor: '#ffffff' }}>
                <Typography variant="caption" color="text.secondary" fontWeight={700} fontSize="0.7rem">
                  LAST HOMEWORK
                </Typography>
                <Typography variant="body2" fontWeight={700} color="#2563eb" noWrap>
                  {repData.student_diary_last_used_value || repData.homework_last_used || '-'}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Paper elevation={0} sx={{ p: 1.8, borderRadius: 2, border: '1px solid #e2e8f0', backgroundColor: '#ffffff' }}>
                <Typography variant="caption" color="text.secondary" fontWeight={700} fontSize="0.7rem">
                  LAST FEES COLLECTED
                </Typography>
                <Typography variant="body2" fontWeight={700} color="#b45309" noWrap>
                  {repData.fee_collection_last_used_value || '-'}
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* Categorized Detailed Feature Health */}
          <Grid container spacing={2.5} mb={3}>
            {/* Academics & Classroom */}
            <Grid item xs={12} sm={6}>
              <Paper elevation={0} sx={{ p: 2.2, borderRadius: 2.5, border: '1px solid #e2e8f0', backgroundColor: '#ffffff' }}>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <MenuBookOutlinedIcon sx={{ color: '#2563eb', fontSize: 20 }} />
                  <Typography variant="subtitle2" fontWeight={800} color="#0f172a">
                    Academics & Classroom
                  </Typography>
                </Box>

                <Box display="flex" flexDirection="column" gap={1.2}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" pb={0.8} borderBottom="1px solid #f1f5f9">
                    <Typography variant="body2" color="text.secondary">Attendance Usage</Typography>
                    {renderMetricBadge(repData.student_attendance_last_used_value || repData.attendance_used_percentage, activeRow.is_not_created)}
                  </Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" pb={0.8} borderBottom="1px solid #f1f5f9">
                    <Typography variant="body2" color="text.secondary">Diary / Homework</Typography>
                    {renderMetricBadge(repData.student_diary_last_used_value || repData.diary_used_percentage, activeRow.is_not_created)}
                  </Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" pb={0.8} borderBottom="1px solid #f1f5f9">
                    <Typography variant="body2" color="text.secondary">Exam Schedule</Typography>
                    {renderMetricBadge(repData.exam_schedule_last_used_value || repData.last_exam_used_percentage, activeRow.is_not_created)}
                  </Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" pb={0.8} borderBottom="1px solid #f1f5f9">
                    <Typography variant="body2" color="text.secondary">Exam Marks</Typography>
                    {renderMetricBadge(repData.exam_marks_last_used_value, activeRow.is_not_created)}
                  </Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="text.secondary">Timetable</Typography>
                    {renderMetricBadge(repData.timetable_last_used_value, activeRow.is_not_created)}
                  </Box>
                </Box>
              </Paper>
            </Grid>

            {/* Finance & Operations */}
            <Grid item xs={12} sm={6}>
              <Paper elevation={0} sx={{ p: 2.2, borderRadius: 2.5, border: '1px solid #e2e8f0', backgroundColor: '#ffffff' }}>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <AccountBalanceWalletOutlinedIcon sx={{ color: '#0d9488', fontSize: 20 }} />
                  <Typography variant="subtitle2" fontWeight={800} color="#0f172a">
                    Finance & Operations
                  </Typography>
                </Box>

                <Box display="flex" flexDirection="column" gap={1.2}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" pb={0.8} borderBottom="1px solid #f1f5f9">
                    <Typography variant="body2" color="text.secondary">Fee Collection</Typography>
                    {renderMetricBadge(repData.fee_collection_last_used_value, activeRow.is_not_created)}
                  </Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" pb={0.8} borderBottom="1px solid #f1f5f9">
                    <Typography variant="body2" color="text.secondary">Fee Plan Done</Typography>
                    {renderMetricBadge(repData.fee_plan_done_value, activeRow.is_not_created)}
                  </Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" pb={0.8} borderBottom="1px solid #f1f5f9">
                    <Typography variant="body2" color="text.secondary">Staff Attendance</Typography>
                    {renderMetricBadge(repData.staff_attendance_last_marked_value, activeRow.is_not_created)}
                  </Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" pb={0.8} borderBottom="1px solid #f1f5f9">
                    <Typography variant="body2" color="text.secondary">Items Sold (Store)</Typography>
                    {renderMetricBadge(repData.item_sold_last_marked_value, activeRow.is_not_created)}
                  </Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="text.secondary">Expenses</Typography>
                    {renderMetricBadge(repData.expenses_last_marked_value, activeRow.is_not_created)}
                  </Box>
                </Box>
              </Paper>
            </Grid>

            {/* Active Users & Engagement */}
            <Grid item xs={12} sm={6}>
              <Paper elevation={0} sx={{ p: 2.2, borderRadius: 2.5, border: '1px solid #e2e8f0', backgroundColor: '#ffffff' }}>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <PeopleAltOutlinedIcon sx={{ color: '#7c3aed', fontSize: 20 }} />
                  <Typography variant="subtitle2" fontWeight={800} color="#0f172a">
                    Active Users & Engagement
                  </Typography>
                </Box>

                <Box display="flex" flexDirection="column" gap={1.2}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" pb={0.8} borderBottom="1px solid #f1f5f9">
                    <Typography variant="body2" color="text.secondary">Staff Active (Past 7 Days)</Typography>
                    {renderMetricBadge(repData.staff_active_last_7_days_value, activeRow.is_not_created)}
                  </Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" pb={0.8} borderBottom="1px solid #f1f5f9">
                    <Typography variant="body2" color="text.secondary">Student Active (Past 7 Days)</Typography>
                    {renderMetricBadge(repData.student_active_last_7_days_value, activeRow.is_not_created)}
                  </Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" pb={0.8} borderBottom="1px solid #f1f5f9">
                    <Typography variant="body2" color="text.secondary">Staff Login Percentage</Typography>
                    {renderMetricBadge(repData.staff_login_percentage_value, activeRow.is_not_created)}
                  </Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="text.secondary">Student Login Percentage</Typography>
                    {renderMetricBadge(repData.student_login_percentage_value, activeRow.is_not_created)}
                  </Box>
                </Box>
              </Paper>
            </Grid>

            {/* Admissions & Inquiries */}
            <Grid item xs={12} sm={6}>
              <Paper elevation={0} sx={{ p: 2.2, borderRadius: 2.5, border: '1px solid #e2e8f0', backgroundColor: '#ffffff' }}>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <FactCheckOutlinedIcon sx={{ color: '#ea580c', fontSize: 20 }} />
                  <Typography variant="subtitle2" fontWeight={800} color="#0f172a">
                    Admissions & Inquiries
                  </Typography>
                </Box>

                <Box display="flex" flexDirection="column" gap={1.2}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" pb={0.8} borderBottom="1px solid #f1f5f9">
                    <Typography variant="body2" color="text.secondary">Enquiry Form Used</Typography>
                    {renderMetricBadge(repData.enquiry_form_used_value, activeRow.is_not_created)}
                  </Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" pb={0.8} borderBottom="1px solid #f1f5f9">
                    <Typography variant="body2" color="text.secondary">Last Enquiry Date</Typography>
                    {renderMetricBadge(repData.enquiry_last_used_value, activeRow.is_not_created)}
                  </Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" pb={0.8} borderBottom="1px solid #f1f5f9">
                    <Typography variant="body2" color="text.secondary">Application Form Used</Typography>
                    {renderMetricBadge(repData.application_form_used_value, activeRow.is_not_created)}
                  </Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="text.secondary">Admission Form Used</Typography>
                    {renderMetricBadge(repData.admission_form_used_value, activeRow.is_not_created)}
                  </Box>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </>
      )}

      {/* Collapsible Section for Technical Synced Columns */}
      {reportColumns && reportColumns.length > 0 && (
        <Paper elevation={0} sx={{ borderRadius: 2.5, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
          <Box
            sx={{
              p: 1.5,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              cursor: 'pointer',
              backgroundColor: '#f8fafc',
              '&:hover': { backgroundColor: '#f1f5f9' },
            }}
            onClick={() => setShowTechnicalDetails(!showTechnicalDetails)}
          >
            <Typography variant="subtitle2" fontWeight={700} color="#334155">
              Detailed Synced Report Fields ({reportColumns.length} fields)
            </Typography>
            <IconButton size="small">
              {showTechnicalDetails ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          </Box>

          <Collapse in={showTechnicalDetails} timeout="auto" unmountOnExit>
            <Box sx={{ p: 2.5, backgroundColor: '#ffffff' }}>
              <Grid container spacing={2}>
                {reportColumns.map((col) => {
                  const val = repData[col.schemacolumn];
                  return (
                    <Grid item xs={12} sm={6} md={4} key={col.schemacolumn}>
                      <Box sx={{ p: 1.2, borderRadius: 1.5, bgcolor: '#f8fafc', border: '1px solid #eef2f7' }}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {col.column}
                        </Typography>
                        <Typography variant="body2" fontWeight={600} color="#0f172a" sx={{ wordBreak: 'break-word' }}>
                          {val === null || val === undefined || val === '' ? '—' : String(val)}
                        </Typography>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          </Collapse>
        </Paper>
      )}
    </Box>
  );
}
